import * as React from 'react';
import { useCurrentUser } from '@klareospace/pharos-lib-front-utils';
import type { CurrentUserResponse, ResolvedUserScope, SiteType } from '@klareospace/pharos-lib-types';
import { useMasterContext } from '&/master';
import { useUserScopes } from '&/hooks/use-user-scopes';
import { useAuth } from '&/auth';
import { useSite } from '../settings/sites/hooks/use-site';
import { useSpace } from '../settings/spaces/hooks/use-space';
import { MainSelectorBreadcrumbView } from './components/breadcrumb';
import { MainSelectorDropdown } from './components/dropdown';
import { useTenantProfile } from './hooks/use-tenant-profile';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MainSelectorViewProps {
  backendUser: CurrentUserResponse | null;
  canReadSites: boolean;
  canReadSpaces: boolean;
  hasMultipleSites: boolean;
  hasMultipleSpaces: boolean;
  singleSiteId: string | null;
  singleSpaceId: string | null;
  siteColor?: string;
  siteName?: string;
  siteType?: SiteType;
  spaceName?: string;
  tenantName: string;
}

interface MainSelectorData {
  backendUser: CurrentUserResponse | null;
  canReadSites: boolean;
  canReadSpaces: boolean;
  hasMultipleSites: boolean;
  hasMultipleSpaces: boolean;
  isTenantLoading: boolean;
  singleSiteId: string | null;
  singleSpaceId: string | null;
  siteColor?: string;
  siteName?: string;
  siteType?: SiteType;
  spaceName?: string;
  tenantName?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    event.currentTarget.click();
  }
}

/**
 * Finds a space name from resolved scopes — checks for a direct space-level match first,
 * then falls back to parentSpaceName from a site-level scope.
 */
function findSpaceNameInScopes(scopes: ResolvedUserScope[], spaceId: string): string | undefined {
  const directScope = scopes.find((scope) => scope.id === spaceId && scope.level === 'space');
  if (directScope) return directScope.name;
  const parentScope = scopes.find((scope) => scope.parentSpaceId === spaceId);
  return parentScope?.parentSpaceName;
}

/**
 * Derives the space name from a user's resolvedScopes.
 * Works for all roles including drivers/carriers who lack spaces:list.
 */
function resolveSpaceNameFromScopes(
  backendUser: CurrentUserResponse | null,
  spaceId: string | null,
): string | undefined {
  if (!backendUser?.resolvedScopes || !spaceId) return undefined;
  return findSpaceNameInScopes(backendUser.resolvedScopes, spaceId);
}

/**
 * Derives the site name from a user's resolvedScopes.
 */
function resolveSiteNameFromScopes(backendUser: CurrentUserResponse | null, siteId: string | null): string | undefined {
  if (!backendUser?.resolvedScopes || !siteId) return undefined;
  const siteScope = backendUser.resolvedScopes.find((scope) => scope.id === siteId && scope.level === 'site');
  return siteScope?.name;
}

// ─── Data hook ────────────────────────────────────────────────────────────────

interface DeriveNamesParams {
  backendUser: CurrentUserResponse | null;
  siteApiName: string | undefined;
  siteId: string | null;
  spaceApiName: string | undefined;
  spaceId: string | null;
}

function deriveNames({ backendUser, siteApiName, siteId, spaceApiName, spaceId }: DeriveNamesParams): {
  siteName?: string;
  spaceName?: string;
} {
  const spaceName = spaceApiName ?? resolveSpaceNameFromScopes(backendUser, spaceId);
  const siteName = siteApiName ?? resolveSiteNameFromScopes(backendUser, siteId);
  return { siteName, spaceName };
}

function useMainSelectorData(isAuthenticated: boolean): MainSelectorData {
  const { user: backendUser } = useCurrentUser(isAuthenticated);
  const resolvedUser = backendUser ?? null;
  const { siteId, spaceId } = useMasterContext();
  const resolvedSpaceId = spaceId ?? '';
  const { data: tenantProfile, isLoading: isTenantLoading } = useTenantProfile();
  const {
    canAccessSites: canReadSites,
    canAccessSpaces: canReadSpaces,
    hasMultipleSites,
    hasMultipleSpaces,
    singleSiteId,
    singleSpaceId,
  } = useUserScopes(isAuthenticated ? resolvedUser : null);
  const { data: site } = useSite(siteId, canReadSites);
  const { data: space } = useSpace(resolvedSpaceId, canReadSpaces);

  // Space name derived from resolvedScopes — works for all roles.
  // Site name prefers API data (for users with sites:list), falls back to resolvedScopes.
  const { siteName, spaceName } = deriveNames({
    backendUser: resolvedUser,
    siteApiName: site?.name,
    siteId,
    spaceApiName: space?.name,
    spaceId,
  });

  return {
    backendUser: resolvedUser,
    canReadSites,
    canReadSpaces,
    hasMultipleSites,
    hasMultipleSpaces,
    isTenantLoading,
    singleSiteId,
    singleSpaceId,
    siteColor: site?.color,
    siteName,
    siteType: site?.type,
    spaceName,
    tenantName: tenantProfile?.name,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MainSelector(): React.JSX.Element | null {
  const { isAuthenticated } = useAuth();
  const data = useMainSelectorData(isAuthenticated);

  if (data.isTenantLoading || !data.tenantName) {
    return null;
  }

  return (
    <MainSelectorView
      backendUser={data.backendUser}
      canReadSites={data.canReadSites}
      canReadSpaces={data.canReadSpaces}
      hasMultipleSites={data.hasMultipleSites}
      hasMultipleSpaces={data.hasMultipleSpaces}
      singleSiteId={data.singleSiteId}
      singleSpaceId={data.singleSpaceId}
      siteColor={data.siteColor}
      siteName={data.siteName}
      siteType={data.siteType}
      spaceName={data.spaceName}
      tenantName={data.tenantName}
    />
  );
}

// ─── Private view component ───────────────────────────────────────────────────

function MainSelectorView({
  backendUser,
  canReadSites,
  canReadSpaces,
  hasMultipleSites,
  hasMultipleSpaces,
  singleSiteId,
  singleSpaceId,
  siteColor,
  siteName,
  siteType,
  spaceName,
  tenantName,
}: MainSelectorViewProps): React.JSX.Element {
  // Single-site user: non-interactive breadcrumb — purely informational, no dropdown
  if (!hasMultipleSpaces && !hasMultipleSites && singleSiteId) {
    return (
      <MainSelectorBreadcrumbView
        interactive={false}
        siteColor={siteColor}
        siteName={siteName}
        siteType={siteType}
        spaceName={spaceName}
        tenantName={tenantName}
      />
    );
  }

  // Single-space user who cannot browse sites (e.g. carrier/driver with space-level scope):
  // non-interactive breadcrumb — they have no site selection capability.
  if (!hasMultipleSpaces && !canReadSites && singleSpaceId && !singleSiteId) {
    return (
      <MainSelectorBreadcrumbView
        interactive={false}
        siteColor={siteColor}
        siteName={siteName}
        siteType={siteType}
        spaceName={spaceName}
        tenantName={tenantName}
      />
    );
  }

  return (
    <MainSelectorDropdown
      backendUser={backendUser}
      canReadSites={canReadSites}
      canReadSpaces={canReadSpaces}
      hasMultipleSpaces={hasMultipleSpaces}
      singleSpaceId={singleSpaceId}
      spaceName={spaceName}
    >
      <div className="group/trigger" onKeyDown={handleTriggerKeyDown} role="button" tabIndex={0}>
        <MainSelectorBreadcrumbView
          siteColor={siteColor}
          siteName={siteName}
          siteType={siteType}
          spaceName={spaceName}
          tenantName={tenantName}
        />
      </div>
    </MainSelectorDropdown>
  );
}
